import { useRef } from "react";

import { useNotificationCenter } from "../NotificationCenter.context";

import { NotificationCenterEmptyState } from "./NotificationCenterEmptyState";
import { NotificationCenterError } from "./NotificationCenterError";
import { NotificationCenterItem } from "./NotificationCenterItem";
import { NotificationCenterLoadMoreSentinel } from "./NotificationCenterLoadMoreSentinel";
import { NotificationCenterSkeleton } from "./NotificationCenterSkeleton";

type Props = {
  emptyTitle?: string;
  emptyDescription?: string;
  errorTitle?: string;
  errorDescription?: string;
  retryLabel?: string;
  skeletonCount?: number;
};

export const NotificationCenterList = ({
  emptyTitle,
  emptyDescription,
  errorTitle,
  errorDescription,
  retryLabel,
  skeletonCount,
}: Props) => {
  const { items, hasMore, isLoadingMore, onLoadMore, isLoading, error } =
    useNotificationCenter();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  if (error && items.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <NotificationCenterError
          description={errorDescription}
          retryLabel={retryLabel}
          title={errorTitle}
        />
      </div>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto">
        <NotificationCenterSkeleton count={skeletonCount} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <NotificationCenterEmptyState
          description={emptyDescription}
          title={emptyTitle}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto" ref={scrollContainerRef}>
      <ul
        className="flex flex-col divide-y divide-border/primary"
        data-testid="notification-center-list"
      >
        {items.map((notification) => (
          <li key={notification.id}>
            <NotificationCenterItem notification={notification} />
          </li>
        ))}
      </ul>
      {hasMore && onLoadMore ? (
        <NotificationCenterLoadMoreSentinel
          isLoadingMore={Boolean(isLoadingMore)}
          onLoadMore={onLoadMore}
          scrollRootRef={scrollContainerRef}
        />
      ) : null}
    </div>
  );
};
